import { API_ENDPOINTS } from '../constants/api';
import apiClient from '../config/axiosInterceptors';
import { ResponseHandler } from '../config/responseHandler';
import { processContentWithSignedUrls, processFeedWithSignedUrls, getSignedUrls, processUsersWithSignedUrls } from '../utils/fileOperations';
import { TIMEOUT_CONFIG } from '../constants/common';
import { reportError } from '../sentry';

const isValidFileKey = (key) => key && typeof key === 'string' && key.trim() !== '' && !key.startsWith('http');

class ContentService {
  /**
   * Fetch content creators
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of creators to fetch
   * @param {number} params.skip - Number of creators to skip
   * @param {string} params.sortBy - Sort order
   * @param {boolean} params.isGuest - Whether user is guest or authenticated
   * @returns {Promise<Object>} Response data containing creators
   */
  async getContentCreators(params = {}) {
    try {
      const { search = '', limit = 6, skip = 0, sortBy = '', isGuest = false } = params;

      // Build query params object conditionally
      const queryParamsObj = {
        limit: limit.toString(),
        skip: skip.toString()
      };

      if (search) {
        queryParamsObj.search = search;
      }

      if (sortBy) {
        queryParamsObj.sortBy = sortBy;
      }

      const queryParams = new URLSearchParams(queryParamsObj);

      const endpoint = isGuest
        ? `${API_ENDPOINTS.CONTENT_CREATOR_GUEST}?${queryParams}`
        : `${API_ENDPOINTS.CONTENT_CREATOR}?${queryParams}`;

      const response = await apiClient.get(endpoint);

      // Process users with signed URLs for profile pictures
      const processedResponse = await processUsersWithSignedUrls(response, 'data');

      return ResponseHandler.handleSuccess(processedResponse);
    } catch (error) {
      return ResponseHandler.handleError(error);
    }
  }

  /**
   * Extract categories from creator data
   * @param {Object} creatorData - Creator data object
   * @returns {Array} Array of creator categories
   */
  extractCreatorCategories(creatorData) {
    if (!creatorData) return [];

    // Check for categories in different possible locations
    const categories = creatorData.categories ||
      creatorData.user_data?.categories ||
      creatorData.profile_tags ||
      creatorData.profileTags ||
      [];

    // Ensure it's an array and filter out empty values
    const validCategories = Array.isArray(categories) ? categories.filter(cat => cat && cat.trim() !== '') : [];

    // Return only first 5 categories if more than 5 exist
    return validCategories.length > 5 ? validCategories.slice(0, 5) : validCategories;
  }

  /**
   * Add creator categories to user's category list
   * @param {Object} creatorData - Creator data object
   * @param {Array} userCategories - Current user categories (optional)
   * @returns {Promise<Object>} Response with added categories info
   */
  async addCreatorCategories(creatorData, userCategories = []) {
    try {
      const creatorCategories = this.extractCreatorCategories(creatorData);

      if (!creatorCategories || creatorCategories.length === 0) {
        return ResponseHandler.handleSuccess(
          { data: { message: 'No categories to add', addedCategories: [] } },
          'No categories to add'
        );
      }

      // Get current user categories from API if not provided
      let currentUserCategories = userCategories;

      if (!currentUserCategories || currentUserCategories.length === 0) {
        const accountResponse = await apiClient.get(API_ENDPOINTS.FETCH_USER);
        if (accountResponse.data?.data) {
          currentUserCategories = accountResponse.data.data.categories || [];
        }
      }

      // Merge categories, removing duplicates
      const mergedCategories = [...new Set([...currentUserCategories, ...creatorCategories])];

      // Determine which categories are new
      const addedCategories = creatorCategories.filter(cat => !currentUserCategories.includes(cat));

      // Update user categories via API (only categories)
      const response = await apiClient.put(
        API_ENDPOINTS.UPDATE_NORMAL_USER,
        {
          categories: mergedCategories,
          ...(creatorData?._id && { searchCreatorId: creatorData?._id })
        }
      );

      // Add metadata about which categories were added
      return ResponseHandler.handleSuccess(
        { ...response, data: { ...response.data, addedCategories } },
        'Categories updated successfully'
      );
    } catch (error) {
      return ResponseHandler.handleError(error);
    }
  }

  /**
   * Fetch content by creator
   * @param {boolean} isAuthenticated - Whether user is authenticated
   * @param {string} creatorId - Creator ID
   * @param {string} type - Content type (feed, media, free)
   * @param {number} limit - Number of items to fetch
   * @param {number} skip - Number of items to skip
   * @param {string} filter - Optional filter parameter
   * @returns {Promise<Object>} Response data containing content
   */
  async getContentByCreator(isAuthenticated, creatorId, type, limit, skip, filter = null) {
    try {
      // Validate creator ID
      if (!creatorId || creatorId === '' || creatorId === 'undefined' || creatorId === 'null') {
        return ResponseHandler.handleError({ message: 'Creator ID is required' });
      }

      // Build query params
      const queryParamsObj = {
        content_type: type,
        limit: limit.toString(),
        skip: skip.toString()
      };

      // Add filter only if provided and not for free/media types
      if (filter && type !== 'free' && type !== 'media' && type !== 'feed') {
        queryParamsObj.filter = filter;
      }

      const queryParams = new URLSearchParams(queryParamsObj);
      const endpoint = `${API_ENDPOINTS.CONTENT_CREATOR}/${creatorId}/contents?${queryParams}`;

      const response = await apiClient.get(endpoint);

      // Process content with signed URLs before restructuring
      // The content is at response.data.data.content
      const processedResponse = await processContentWithSignedUrls(response, 'data.content');

      // Automatically trigger thumbnail creation for video assets missing posters
      if (isAuthenticated) {
        try {
          const contentItems = Array.isArray(processedResponse?.data?.data?.content)
            ? processedResponse.data.data.content
            : [];

          contentItems.forEach((item) => {
            if (!item || !Array.isArray(item.assets)) {
              return;
            }

            const hasMissingVideoPoster = item.assets.some(
              (asset) => asset?.type === 'video' && !asset?.poster
            );

            if (hasMissingVideoPoster && item._id) {
              // Fire-and-forget thumbnail creation; don't block main response
              this.createThumbnailForContent(item._id).catch((error) => {
                reportError(error, 'thumbnail create content', { extra: { contentId: item._id } });
              });
            }
          });
        } catch (thumbnailError) {
          reportError(thumbnailError, 'thumbnail check content items');
        }
      }

      // Restructure response if content is nested
      if (processedResponse.data.data.content) {
        processedResponse.data = processedResponse.data.data.content;
      }

      const handledResponse = ResponseHandler.handleSuccess(processedResponse);

      return handledResponse;
    } catch (error) {
      return ResponseHandler.handleError(error);
    }
  }

  /**
   * Fetch content counts for a creator
   * @param {string} contentCreatorId - Creator ID
   * @returns {Promise<Object>} Content counts response
   */
  async fetchContentCounts(contentCreatorId) {
    try {
      const response = await apiClient.get(`${API_ENDPOINTS.CREATOR_CONTENT_COUNTS}?contentCreatorId=${contentCreatorId}`);
      return ResponseHandler.handleSuccess(response, 'Content counts fetched successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to fetch content counts');
    }
  }

  /**
   * Fetch a single content item by creator and content id
   * @param {string} creatorId - Creator ID
   * @param {string} contentId - Content ID
   * @returns {Promise<Object>} Content response
   */
  async getCreatorContentById(creatorId, contentId) {
    try {
      if (!creatorId) {
        return ResponseHandler.handleError({ message: 'Creator ID is required' }, 'Creator ID is required');
      }

      if (!contentId) {
        return ResponseHandler.handleError({ message: 'Content ID is required' }, 'Content ID is required');
      }

      const endpoint = `${API_ENDPOINTS.CONTENT_CREATORS}/${creatorId}/content?content_id=${contentId}`;
      const response = await apiClient.get(endpoint);

      const originalData = response?.data?.data;
      const contentItems = Array.isArray(originalData) ? originalData : originalData ? [originalData] : [];

      const fileKeys = [];

      const collectFileKeys = (item) => {
        if (!item || typeof item !== 'object') {
          return;
        }

        if (Array.isArray(item.assets)) {
          item.assets.forEach((asset) => {
            if (isValidFileKey(asset?.key)) {
              fileKeys.push(asset.key);
            }

            // Also collect poster keys for video assets so we can sign them
            if (asset?.type === 'video' && isValidFileKey(asset?.poster)) {
              fileKeys.push(asset.poster);
            }
          });
        }

        if (item.content && typeof item.content === 'object') {
          collectFileKeys(item.content);
        }
      };

      contentItems.forEach(collectFileKeys);

      let enhancedItems = contentItems;

      if (fileKeys.length > 0) {
        const uniqueFileKeys = [...new Set(fileKeys)];
        const signedUrlsData = await getSignedUrls(uniqueFileKeys, false);
        const signedUrlMap = {};
        signedUrlsData.forEach(({ key, signedUrl }) => {
          signedUrlMap[key] = signedUrl;
        });

        const applySignedUrls = (item) => {
          if (!item || typeof item !== 'object') {
            return item;
          }

          const updatedItem = { ...item };

          if (Array.isArray(updatedItem.assets)) {
            updatedItem.assets = updatedItem.assets.map((asset) => {
              if (!asset || typeof asset !== 'object') {
                return asset;
              }

              const updatedAsset = { ...asset };

              // Replace main asset key with signed URL
              if (isValidFileKey(updatedAsset.key) && signedUrlMap[updatedAsset.key]) {
                updatedAsset.key = signedUrlMap[updatedAsset.key];
              }

              // Replace poster key with signed URL for video assets
              if (updatedAsset.type === 'video' && isValidFileKey(updatedAsset.poster) && signedUrlMap[updatedAsset.poster]) {
                updatedAsset.poster = signedUrlMap[updatedAsset.poster];
              }

              return updatedAsset;
            });
          }

          if (updatedItem.content && typeof updatedItem.content === 'object') {
            updatedItem.content = applySignedUrls(updatedItem.content);
          }

          return updatedItem;
        };

        enhancedItems = contentItems.map(applySignedUrls);
      }

      const modifiedResponse = {
        ...response,
        data: {
          ...response.data,
          data: Array.isArray(originalData) ? enhancedItems : enhancedItems[0] || originalData
        }
      };

      return ResponseHandler.handleSuccess(modifiedResponse, 'Content fetched successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to fetch content');
    }
  }

  /**
   * Trigger thumbnail creation for a specific content item
   * This is used when a video asset has no poster key.
   * @param {string} contentId - Content ID
   * @returns {Promise<Object>|void} Thumbnail creation response
   */
  async createThumbnailForContent(contentId) {
    try {
      if (!contentId) {
        return;
      }

      const requestBody = {
        content_id: contentId,
      };

      const response = await apiClient.put(
        API_ENDPOINTS.CREATOR_CONTENT_THUMBNAIL,
        requestBody,
        {
          timeout: TIMEOUT_CONFIG.THUMBNAIL,
        }
      );

      return ResponseHandler.handleSuccess(response, 'Thumbnail creation triggered');
    } catch (error) {
      reportError(error, 'trigger thumbnail creation');
      return ResponseHandler.handleError(error, 'Failed to trigger thumbnail creation');
    }
  }

  /**
   * Delete a content item by id
   * @param {string} contentId
   * @returns {Promise<Object>} delete response
   */
  async deleteContent(contentId) {
    try {
      if (!contentId) {
        return ResponseHandler.handleError({ message: 'content_id is required' });
      }

      // Backend expects JSON body with { content_id }
      const response = await apiClient.delete(API_ENDPOINTS.CREATOR_CONTENT, {
        data: { content_id: contentId },
      });
      return ResponseHandler.handleSuccess(response, 'Content deleted successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to delete content');
    }
  }

  /**
   * Update a content item by id
   * @param {string} contentId
   * @param {Object} data - Fields to update (e.g. { lock_content: false, price: null })
   * @returns {Promise<Object>} update response
   */
  async updateContent(contentId, data = {}) {
    try {
      if (!contentId) {
        return ResponseHandler.handleError({ message: 'content_id is required' });
      }

      const requestBody = {
        content_id: contentId,
        ...data,
      };

      const response = await apiClient.put(API_ENDPOINTS.CREATOR_CONTENT, requestBody);
      return ResponseHandler.handleSuccess(response, 'Content updated successfully');
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to update content');
    }
  }

  /**
    * Fetch content by creator
    * @param {string} creatorId - Creator ID
    * @param {string} type - Content type (feed, media, free)
    * @param {number} limit - Number of items to fetch
    * @param {number} skip - Number of items to skip
    * @param {string} filter - Optional filter parameter
    * @returns {Promise<Object>} Response data containing content
    */
  async getFeedssfCreator(creatorId, type, limit, skip, filter = null) {
    try {
      // Validate creator ID
      if (!creatorId || creatorId === '' || creatorId === 'undefined' || creatorId === 'null') {
        return ResponseHandler.handleError({ message: 'Creator ID is required' });
      }

      // Build query params
      const queryParamsObj = {
        limit: limit.toString(),
        skip: skip.toString()
      };

      // Add filter only if provided and not for free/media types
      if (filter && type !== 'free' && type !== 'media' && type !== 'feed') {
        queryParamsObj.filter = filter;
      }

      const queryParams = new URLSearchParams(queryParamsObj);
      const endpoint = `${API_ENDPOINTS.CONTENT_CREATORS}/${creatorId}/feed?${queryParams}`;

      const response = await apiClient.get(endpoint);

      // Process content with signed URLs (media/images)
      const processedResponse = await processContentWithSignedUrls(response, 'data');

      // Also process profile pictures with signed URLs
      const finalResponse = await processFeedWithSignedUrls(processedResponse, 'data');

      const handledResponse = ResponseHandler.handleSuccess(finalResponse);

      return handledResponse;
    } catch (error) {
      return ResponseHandler.handleError(error);
    }
  }

  /**
   * Fetch user feed
   * @param {number} limit - Number of items to fetch
   * @param {number} skip - Number of items to skip (page number)
   * @returns {Promise<Object>} Response data containing feed
   */
  async getFeed(limit = 9, skip = 0) {
    try {
      // Validate parameters
      if (limit < 1) {
        return ResponseHandler.handleError({ message: 'Limit must be greater than 0' });
      }

      if (skip < 0) {
        return ResponseHandler.handleError({ message: 'Skip must be greater than or equal to 0' });
      }

      // Build query params
      const queryParams = new URLSearchParams({
        limit: limit.toString(),
        skip: skip.toString()
      });

      const endpoint = `${API_ENDPOINTS.FEED}?${queryParams}`;
      const response = await apiClient.get(endpoint, {
        timeout: TIMEOUT_CONFIG.FEED,
      });

      // Extract recordsTotal before processing (it's at response.data.recordsTotal)
      const recordsTotal = response?.data?.recordsTotal || 0;

      // Process content with signed URLs (media/images)
      const processedContentResponse = await processContentWithSignedUrls(response, 'data');

      // Process feed with signed URLs for profile pictures
      const processedResponse = await processFeedWithSignedUrls(processedContentResponse, 'data');

      const handledResponse = ResponseHandler.handleSuccess(processedResponse);

      // Preserve recordsTotal in the response data
      if (handledResponse.success && handledResponse.data) {
        handledResponse.data = {
          data: Array.isArray(handledResponse.data) ? handledResponse.data : [],
          recordsTotal: recordsTotal
        };
      }

      return handledResponse;
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to fetch feed');
    }
  }

  /**
   * Fetch content creator list with unified filtering
   * @param {Object} params - Query parameters
   * @param {string} params.searchQuery - Search query string
   * @param {number} params.limit - Number of creators to fetch (default: 5, ignored if searchQuery exists)
   * @param {boolean} params.isTrending - Whether to fetch trending creators (default: false)
   * @param {boolean} params.isFeature - Whether to fetch featured creators (default: false)
   * @param {string} params.userId - User ID
   * @returns {Promise<Object>} Response data containing creators
   */
  async getContentCreatorList(params = {}) {
    try {
      const {
        searchQuery = '',
        limit = 20,
        isTrending = false,
        isFeature = false,
        userId = null
      } = params;

      // Build query params object
      const queryParamsObj = {};

      // Add search query if provided
      if (searchQuery && searchQuery.trim()) {
        queryParamsObj.search = encodeURIComponent(searchQuery.trim());
      }

      if (limit) {
        // Only add limit if no search query (for random selection)
        queryParamsObj.limit = limit.toString();
      }

      if (isTrending) {
        queryParamsObj.isTrending = true;
      }

      if (isFeature) {
        queryParamsObj.isFeature = true;
      }

      if (userId) {
        queryParamsObj.userId = userId;
      }

      const queryParams = new URLSearchParams(queryParamsObj);
      const endpoint = `${API_ENDPOINTS.CONTENT_CREATOR_LIST}?${queryParams}`;

      const response = await apiClient.get(endpoint);

      // Process users with signed URLs for profile pictures
      const processedResponse = await processUsersWithSignedUrls(response, 'data');

      return ResponseHandler.handleSuccess(processedResponse);
    } catch (error) {
      return ResponseHandler.handleError(error);
    }
  }

  /**
   * Fetch purchased content
   * @param {number} limit - Number of items to fetch (default: 6)
   * @param {number} page - Page number (default: 0)
   * @returns {Promise<Object>} Response data containing purchased content
   */
  async getPurchasedContent(limit = 6, page = 0) {
    try {
      // Validate parameters
      if (limit < 1) {
        return ResponseHandler.handleError({ message: 'Limit must be greater than 0' });
      }

      if (page < 0) {
        return ResponseHandler.handleError({ message: 'Page must be greater than or equal to 0' });
      }

      // Build URL with exact format as specified
      const endpoint = `${process.env.REACT_APP_API}/contents/purchase?limit=${limit}&skip=${page}`;
      const response = await apiClient.get(endpoint);

      // Helper function to check if a key is a valid file key (not a URL)
      const isValidFileKey = (key) => {
        return key && typeof key === 'string' && key.trim() !== '' && !key.startsWith('http');
      };

      // Extract file keys from nested content structure (video src + poster thumbnails)
      const fileKeys = [];
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        response.data.data.forEach(item => {
          if (item.content?.assets && Array.isArray(item.content.assets)) {
            item.content.assets.forEach(asset => {
              if (isValidFileKey(asset.key)) {
                fileKeys.push(asset.key);
              }
              if (isValidFileKey(asset.poster)) {
                fileKeys.push(asset.poster);
              }
            });
          }
        });
      }

      // Get signed URLs for all file keys
      let signedUrlMap = {};
      if (fileKeys.length > 0) {
        const uniqueFileKeys = [...new Set(fileKeys)];
        try {
          const signedUrlsData = await getSignedUrls(uniqueFileKeys, false);
          signedUrlsData.forEach(item => {
            signedUrlMap[item.key] = item.signedUrl;
          });
        } catch (error) {
          reportError(error, 'signed URLs content');
        }
      }

      // Replace file keys with signed URLs in the response
      const modifiedResponse = { ...response };
      if (modifiedResponse.data && modifiedResponse.data.data && Array.isArray(modifiedResponse.data.data)) {
        modifiedResponse.data.data = modifiedResponse.data.data.map(item => {
          const modifiedItem = { ...item };

          if (item.content) {
            modifiedItem.content = { ...item.content };

            // Replace asset keys and poster thumbnails with signed URLs
            if (item.content.assets && Array.isArray(item.content.assets)) {
              modifiedItem.content.assets = item.content.assets.map(asset => ({
                ...asset,
                key: signedUrlMap[asset.key] || asset.key,
                poster: signedUrlMap[asset.poster] || asset.poster
              }));
            }
          }

          return modifiedItem;
        });
      }

      // Process feed with signed URLs for profile pictures
      const processedResponse = await processFeedWithSignedUrls(modifiedResponse, 'data');

      const handledResponse = ResponseHandler.handleSuccess(processedResponse);

      // Ensure data structure matches expected format
      if (handledResponse.success && handledResponse.data) {
        // If data is already an array, wrap it in { data: [...] }
        if (Array.isArray(handledResponse.data)) {
          handledResponse.data = {
            data: handledResponse.data
          };
        }
      }

      return handledResponse;
    } catch (error) {
      return ResponseHandler.handleError(error, 'Failed to fetch purchased content');
    }
  }
}

export default new ContentService();